import React, { useEffect, useState } from 'react';

import { toast } from 'react-toastify';

import BusinessIcon from '@mui/icons-material/Business';
import { Card, Stack } from '@mui/material';

import RemoveModal from './components/RemoveModal';
import UploadModal from './components/UploadModal';
import useCompanyImage from './hooks/useCompanyImage';
import * as S from './styles';
import Button from 'components/Button';
import Text from 'components/Text';
import { useEditStatus } from 'global/EditStatusContext';
import { useTab } from 'global/TabContext';
import { useUser } from 'global/UserContext';

import 'react-toastify/dist/ReactToastify.css';

interface CompanyImageProps {
  companyUuid: string;
  onClose?: () => void;
}

const CompanyImage: React.FC<CompanyImageProps> = ({ companyUuid, onClose }) => {
  const { activeTabContext } = useTab();
  const { user } = useUser();
  const { isEditing } = useEditStatus();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [showSavingStatus, setShowSavingStatus] = useState(isEditing);

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { companyImage, updateCompanyImage, deleteCompanyImage, loading } =
    useCompanyImage(companyUuid);

  useEffect(() => {
    if (isEditing) {
      setShowSavingStatus(true);
    } else {
      const timer = setTimeout(() => {
        setShowSavingStatus(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  const handleUpload = (info: any) => {
    const file = info.file;
    if (!file) {
      toast.error('Nenhum arquivo foi selecionado.');
      return;
    }
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Por favor, envie um arquivo de imagem válido.');
      return;
    }

    const maxFileSize = 1024 * 1024 * 5; // 5MB

    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      img.src = e.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const MAX_WIDTH = 1024;
        const MAX_HEIGHT = 1024;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          blob => {
            if (blob && blob.size <= maxFileSize) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now(),
              });

              setSelectedFile(resizedFile);
              setPreviewImage(URL.createObjectURL(resizedFile));
            } else {
              toast.error('A imagem não pôde ser redimensionada abaixo de 500 KB.');
            }
          },
          file.type,
          0.8
        );
      };
    };

    reader.readAsDataURL(file);
  };

  const handleConfirmUpload = () => {
    if (selectedFile) {
      updateCompanyImage(selectedFile);
      setPreviewImage(null);
      setSelectedFile(null);
      toast.success('Logo da empresa atualizada com sucesso!');
    } else {
      toast.error('Nenhum arquivo selecionado.');
    }
  };

  const handleCancelUpload = () => {
    setPreviewImage(null);
    setSelectedFile(null);
    setIsModalVisible(false);
  };

  const handleRemoveImage = () => {
    deleteCompanyImage();
    toast.success('Logo da empresa removida com sucesso!');
    setIsRemoveModalVisible(false);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleCloseRemoveModal = () => {
    setIsRemoveModalVisible(false);
  };

  const handleOpenModal = () => {
    if (!companyUuid) {
      toast.error('Empresa não selecionada');
      return;
    }
    setIsModalVisible(true);
  };

  const renderAvatarOrImage = () => {
    if (previewImage) {
      return (
        <S.PreviewImage>
          <Text size="medium" bold>
            Pré-visualização:
          </Text>
          <img
            src={previewImage}
            alt="Preview"
            style={{ width: 180, height: 180, borderRadius: '50%' }}
          />
        </S.PreviewImage>
      );
    }

    if (companyImage) {
      return (
        <img
          src={companyImage}
          alt="Logo da empresa"
          style={{ width: 180, height: 180, borderRadius: '50%' }}
        />
      );
    }

    return (
      <Stack spacing={1} alignItems="center">
        <S.ProfilePictureWrapper>
          <BusinessIcon
            sx={{
              width: 180,
              height: 180,
              color: 'text.secondary',
              backgroundColor: 'background.paper',
              borderRadius: '50%',
              padding: 2,
              border: '2px dashed',
              borderColor: 'divider',
            }}
          />
        </S.ProfilePictureWrapper>
        <Text size="small" color="textSecondary" textAlign="center">
          Logo da empresa não definido
        </Text>
        <Text size="xSmall" color="textSecondary" textAlign="center">
          Clique em "Adicionar Logo da empresa" para incluir
        </Text>
      </Stack>
    );
  };

  return (
    <Card sx={{ width: '100%', borderRadius: 2 }}>
      <Text size="medium" bold color="textPrimary">
        Logo da Empresa
      </Text>
      <Stack
        width="100%"
        direction="row"
        spacing={2}
        sx={{
          paddingY: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          flex: 1,
        }}
      >
        <S.ProfilePictureWrapper>{renderAvatarOrImage()}</S.ProfilePictureWrapper>

        <Stack direction="column" spacing={1} width="50%">
          <Button type="button" dottedBorder underlineText onClick={handleOpenModal}>
            {companyImage ? 'Alterar Logo da empresa' : 'Adicionar Logo da empresa'}
          </Button>

          <Text size="xSmall" color="textSecondary" textAlign="left">
            O arquivo deve conter no máximo 1MB e ser no formato .jpg ou .png
          </Text>
        </Stack>
      </Stack>

      <UploadModal
        isModalVisible={isModalVisible}
        loading={loading}
        previewImage={previewImage}
        profileImage={companyImage}
        handleCancelUpload={handleCancelUpload}
        handleConfirmUpload={handleConfirmUpload}
        handleUpload={handleUpload}
      />

      <RemoveModal
        isRemoveModalVisible={isRemoveModalVisible}
        loading={loading}
        profileImage={companyImage}
        handleRemoveImage={handleRemoveImage}
        handleCloseRemoveModal={handleCloseRemoveModal}
      />
    </Card>
  );
};

export default CompanyImage;
